import React from 'react';
import { Link } from 'react-router-dom';
import './BlogPost.css';

const BlogPost2 = () => {
  return (
    <div className="blogpost-page">
      <div className="blogpost-container">
        <Link to="/blog" className="blogpost-back">&larr; Back to Blog</Link>

        <div className="blogpost-card">
          <span className="blogpost-category">Career Tips</span>
          <h1 className="blogpost-title">5 Cold Email Templates That Actually Get Responses</h1>
          <p className="blogpost-meta">February 28, 2026 &middot; 5 min read</p>

          <div className="blogpost-body">
            <p>
              Most job advice boils down to "apply online and hope for the best." But here is the thing: a huge portion of jobs, some estimates say up to 70 percent, are never posted publicly. They are filled through internal referrals, recruiter networks, and yes, cold outreach. If you are only applying through job portals, you are competing for a fraction of the available opportunities.
            </p>
            <p>
              Cold emailing recruiters and hiring managers is one of the most underrated strategies in the job search. It feels intimidating, but when done right, it works remarkably well. We have analyzed thousands of outreach emails sent through ApplyAI and identified the patterns that consistently get responses. Here are five templates you can start using today.
            </p>

            <h2>Before We Dive In: The Ground Rules</h2>
            <p>
              Every effective cold email shares a few traits. It is short (under 150 words). It is specific (mentions the company, role, or something personal). It has a clear ask (not just "I wanted to reach out"). And it makes it easy for the reader to say yes.
            </p>
            <p>
              Keep these principles in mind as you customize the templates below for your own situation.
            </p>

            <h2>Template 1: The Direct Ask</h2>
            <p>
              This is the simplest and often the most effective approach. You state who you are, what you want, and ask for a small commitment. The key is specificity: mention the exact role and explain in one sentence why you are a fit.
            </p>
            <blockquote>
              <p>Subject: Quick question about the [Role Title] position</p>
              <p>Hi [Name],</p>
              <p>I am a [year] student at [University] studying [Major], and I recently applied for the [Role Title] position at [Company]. I have been following [Company]'s work on [specific project or product], and the role feels like a strong fit given my experience with [relevant skill or project].</p>
              <p>Would you have 15 minutes this week for a quick call? I would love to learn more about the team and share why I am excited about this opportunity.</p>
              <p>Best,<br />[Your Name]</p>
            </blockquote>
            <p>
              <strong>Why it works:</strong> It is respectful of their time (15 minutes is a small ask), it shows you have done your homework (specific project mention), and it connects your experience to the role. The subject line is clear and creates curiosity without being clickbaity.
            </p>

            <h2>Template 2: The Mutual Connection</h2>
            <p>
              If you share any kind of connection with the recipient, whether it is a university, a previous employer, a conference, or even a LinkedIn group, lead with that. Shared context dramatically increases response rates because it moves you from "stranger" to "someone in my orbit."
            </p>
            <blockquote>
              <p>Subject: Fellow [University] alum interested in [Company]</p>
              <p>Hi [Name],</p>
              <p>I came across your profile and noticed you graduated from [University] as well. I am currently a [year] studying [Major], and I am really interested in the [Role/Team] at [Company].</p>
              <p>I would love to hear about your path from [University] to [Company] and any advice you might have for someone trying to break into [industry/field]. Would you be open to a brief chat sometime this week or next?</p>
              <p>Thanks so much,<br />[Your Name]</p>
            </blockquote>
            <p>
              <strong>Why it works:</strong> The alumni angle immediately creates rapport. People generally want to help others from their school. You are not asking for a job directly, you are asking for advice, which feels lower-stakes and is more likely to get a yes. The conversation naturally leads to opportunities.
            </p>

            <h2>Template 3: The Value Add</h2>
            <p>
              Instead of asking for something, lead by offering something. Maybe you built a project related to what the company does. Maybe you wrote an analysis of their market. Maybe you have a specific skill they need. This flips the dynamic: you are not a job seeker asking for a favor, you are a peer bringing something to the table.
            </p>
            <blockquote>
              <p>Subject: Built something inspired by [Company Product]</p>
              <p>Hi [Name],</p>
              <p>I recently built [brief description of project] as part of my coursework, and it was heavily inspired by what [Company] is doing with [specific product or feature]. I thought you might find it interesting: [link to project or brief description].</p>
              <p>I am actively looking for [internship/full-time] opportunities in [field], and [Company] is at the top of my list. If you have a few minutes, I would love to get your feedback on the project and learn about what your team is working on.</p>
              <p>Best,<br />[Your Name]</p>
            </blockquote>
            <p>
              <strong>Why it works:</strong> You are demonstrating initiative and genuine interest, not just saying you are interested. Sharing a relevant project gives the recipient something concrete to evaluate. It also shows you can do the work, which is ultimately what they care about.
            </p>

            <h2>Template 4: The Follow-Up</h2>
            <p>
              You already applied through the portal. Now you want to make sure your application does not get lost. This email is about reinforcing your candidacy with a human touch. Timing matters here: send it two to three days after submitting your application.
            </p>
            <blockquote>
              <p>Subject: Following up on my application for [Role Title]</p>
              <p>Hi [Name],</p>
              <p>I recently submitted an application for the [Role Title] position at [Company], and I wanted to reach out directly to express my enthusiasm for the role.</p>
              <p>I am particularly drawn to [specific aspect of the role or company], and I believe my background in [relevant experience] aligns well with what the team is looking for. I have attached my resume for convenience.</p>
              <p>I would welcome the chance to discuss the role further if you have a few minutes. Thank you for your time.</p>
              <p>Best,<br />[Your Name]</p>
            </blockquote>
            <p>
              <strong>Why it works:</strong> Most candidates never follow up. By reaching out to the hiring manager directly, you separate yourself from the hundreds of other applications. It is professional, concise, and gives them an easy way to pull your resume from the pile.
            </p>

            <h2>Template 5: The Breakup Email</h2>
            <p>
              You have sent one or two emails with no response. This is your final follow-up. The "breakup email" works because it creates a subtle sense of closure, which psychologically prompts people to respond before the window closes. Keep it light and friendly, never passive-aggressive.
            </p>
            <blockquote>
              <p>Subject: Closing the loop</p>
              <p>Hi [Name],</p>
              <p>I reached out a couple of weeks ago about the [Role Title] position and wanted to send one last note. I completely understand how busy things get, so no worries at all if the timing is not right.</p>
              <p>If there is someone else on the team who might be a better person to connect with, I would be grateful for a point in the right direction. Either way, I appreciate your time and wish you and the team all the best.</p>
              <p>Thanks,<br />[Your Name]</p>
            </blockquote>
            <p>
              <strong>Why it works:</strong> It is gracious and low-pressure. The phrase "closing the loop" signals this is the last email, which often triggers a response. Asking to be redirected gives them an easy out that still helps you. Many of the responses we have seen to breakup emails are some variation of "So sorry I missed this, let me connect you with..."
            </p>

            <h2>Tips for Maximizing Your Response Rate</h2>
            <p>
              <strong>Best times to send:</strong> Tuesday through Thursday, between 8 and 10 AM in the recipient's time zone. Emails sent on Monday morning get buried in the weekend backlog, and Friday emails get forgotten.
            </p>
            <p>
              <strong>Subject lines:</strong> Keep them under 50 characters. Be specific. Avoid anything that sounds like marketing. "Quick question about the SWE internship" beats "Passionate student seeking opportunities" every time.
            </p>
            <p>
              <strong>Follow-up cadence:</strong> Wait five to seven business days between follow-ups. Send a maximum of three emails total. After that, move on. Persistence is good; pestering is not.
            </p>
            <p>
              <strong>Personalization is everything:</strong> A cold email that could be sent to any company at any time will be treated like spam. Mention something specific about the company, the recipient's background, or the role. Even one personalized sentence dramatically increases your chances.
            </p>

            <h2>How ApplyAI Automates This</h2>
            <p>
              Writing five personalized cold emails is manageable. Writing 50 is a full-time job. ApplyAI generates personalized versions of these templates for every company and recruiter you are targeting. It pulls in details about the company, the role, and the recipient's background to craft emails that feel handwritten. You review and approve each one before it sends.
            </p>
            <p>
              The result? Our users send an average of 30 personalized cold emails per week with about 10 minutes of effort. That is the kind of leverage that changes outcomes.
            </p>

            <div className="blogpost-cta">
              <p>
                Ready to automate your cold outreach? <a href="https://applyai.co" target="_blank" rel="noopener noreferrer">Get started free at ApplyAI</a> and start landing in inboxes instead of application portals.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost2;
